/*
  Discovery Box landing page behaviour (templates/page.discovery-box.json).

  - Buy: sign-up dialog (email + consent, sent to Klaviyo), then adds the box (once) and goes to
    checkout, keeping UTM parameters and the consent mark as cart attributes
  - Sticky buy bar once the hero buy box has scrolled away
  - Headline variant from ?headline=a|b|c
  - Dish cards and FAQ: one open at a time
  - Reel: plays muted while in view, tap to unmute
  - Postcode checker and next delivery line, from the shipping windows API the site header uses
  - Tracking: ViewContent on load, scroll depth at each act. AddToCart, InitiateCheckout and
    Purchase come from Shopify's standard events (cart/add.js and checkout), which the Meta,
    TikTok and Google channel apps already forward, so they are not fired again here.
*/
(function () {
  'use strict';

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'ttclid', 'gclid'];
  var UTM_STORE = 'db-utm';
  var POSTCODE_STORE = 'db-postcode';
  var KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=VQ9NTk';
  var KLAVIYO_REVISION = '2025-07-15';
  var KLAVIYO_LIST_ID = 'RS5GV7';
  var STOREFRONT_API_VERSION = '2025-07';
  var CONSENT_ATTRIBUTE = '_db_consent';
  var CONSENT_VALUE = 'yes';
  var WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function storageGet(store, key) {
    try {
      return window[store].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (error) {
      /* private mode: nothing to keep */
    }
  }

  /* ---------- Tracking ---------- */

  function track(name, data) {
    data = data || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, data));
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (error) {
      /* custom pixel bus not available */
    }
  }

  function boxDetails() {
    var form = document.querySelector('[data-db-buy]');
    var price = document.querySelector('.db-buybox .db-price');
    return {
      variantId: form ? form.querySelector('[name="id"]').value : '',
      value: price ? parseFloat(price.textContent.replace(/[^0-9.]/g, '')) || 0 : 0,
    };
  }

  function trackViewContent() {
    var box = boxDetails();
    if (!box.variantId) return;
    var params = { content_ids: [box.variantId], content_type: 'product', value: box.value, currency: 'AUD' };
    if (typeof window.fbq === 'function') window.fbq('track', 'ViewContent', params);
    if (window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track('ViewContent', { content_id: box.variantId, content_type: 'product', value: box.value, currency: 'AUD' });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'view_item', { currency: 'AUD', value: box.value, items: [{ item_id: box.variantId }] });
    }
    track('discovery_box_viewed', params);
  }

  function initScrollDepth() {
    var marks = document.querySelectorAll('[data-db-track]');
    if (!marks.length || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        track('discovery_scroll', { act: entry.target.getAttribute('data-db-track') });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.25 });
    marks.forEach(function (mark) {
      observer.observe(mark);
    });
  }

  /* ---------- UTMs ---------- */

  function captureUtms() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    UTM_KEYS.forEach(function (key) {
      if (params.get(key)) found[key] = params.get(key);
    });
    if (Object.keys(found).length) {
      found.landing_page = window.location.pathname;
      storageSet('sessionStorage', UTM_STORE, JSON.stringify(found));
    }
  }

  function storedUtms() {
    try {
      return JSON.parse(storageGet('sessionStorage', UTM_STORE) || '{}');
    } catch (error) {
      return {};
    }
  }

  /* ---------- Buy ---------- */

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) throw json;
        return json;
      });
    });
  }

  /*
    Every buy button opens the sign-up dialog first. The email goes to Klaviyo, then the cart is marked
    with CONSENT_ATTRIBUTE, which is what the Discovery Box discount checks before giving the $99 price.
    A cart that's already marked skips the dialog, so going back from checkout doesn't ask twice.
  */
  function emailIsValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function subscribeInKlaviyo(email) {
    return fetch(KLAVIYO_SUBSCRIBE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Discovery Box landing page',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { discovery_box_popup: 'true' },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } },
        },
      }),
    }).then(function (response) {
      if (!response.ok) throw new Error('Klaviyo ' + response.status);
    });
  }

  // Fills the email in at checkout, so the order lands on the profile that gave consent
  function setCheckoutEmail(email, cart) {
    return fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query:
          'mutation($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {' +
          ' cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) { cart { id } } }',
        variables: { cartId: 'gid://shopify/Cart/' + cart.token, buyerIdentity: { email: email } },
      }),
    }).catch(function () {
      return null; // the customer can still type it at checkout
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  function addBox(variantId, attributes, email) {
    return getCart()
      .then(function (cart) {
        var inCart = cart.items.some(function (item) {
          return item.variant_id === variantId;
        });
        return inCart ? cart : postJson('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      })
      .then(function () {
        return postJson('/cart/update.js', { attributes: attributes });
      })
      .then(function (cart) {
        return email ? setCheckoutEmail(email, cart) : null;
      })
      .then(function () {
        window.location.href = '/checkout';
      });
  }

  function initBuy() {
    var dialog = document.querySelector('[data-db-consent]');
    var pendingForm = null;

    function showFormError(form, failure) {
      var error = form.querySelector('.db-cta__error');
      form.querySelector('button').removeAttribute('aria-busy');
      if (error) {
        error.textContent = (failure && failure.description) || 'Something went wrong. Please try again.';
        error.hidden = false;
      }
    }

    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-db-buy]');
      if (!form) return;
      event.preventDefault();

      var button = form.querySelector('button');
      var error = form.querySelector('.db-cta__error');
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;

      track('discovery_box_cta', { location: form.getAttribute('data-location') || '' });

      getCart()
        .then(function (cart) {
          if (cart.attributes && cart.attributes[CONSENT_ATTRIBUTE] === CONSENT_VALUE) {
            return addBox(Number(form.querySelector('[name="id"]').value), storedUtms(), null);
          }
          button.removeAttribute('aria-busy');
          pendingForm = form;
          dialog.showModal();
          track('discovery_box_signup_open', { location: form.getAttribute('data-location') || '' });
          return null;
        })
        .catch(function (failure) {
          showFormError(form, failure);
        });
    });

    if (!dialog) return;

    var signup = dialog.querySelector('[data-db-consent-form]');
    var signupError = dialog.querySelector('[data-db-consent-error]');
    var signupButton = signup.querySelector('[type="submit"]');

    function showSignupError(message, field) {
      signupError.textContent = message;
      signupError.hidden = false;
      if (field) field.focus();
    }

    dialog.querySelector('[data-db-consent-close]').addEventListener('click', function () {
      dialog.close();
    });

    // Tap on the dimmed background closes it
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    signup.addEventListener('submit', function (event) {
      event.preventDefault();
      if (signupButton.getAttribute('aria-busy') === 'true') return;
      signupError.hidden = true;

      var email = signup.elements.email.value.trim();
      if (!emailIsValid(email)) {
        showSignupError('Please enter a valid email address.', signup.elements.email);
        return;
      }
      if (!signup.elements.consent.checked) {
        showSignupError('Please tick the box to unlock the offer.', signup.elements.consent);
        return;
      }

      signupButton.setAttribute('aria-busy', 'true');
      var variantId = Number(pendingForm.querySelector('[name="id"]').value);
      var attributes = Object.assign({}, storedUtms());
      attributes[CONSENT_ATTRIBUTE] = CONSENT_VALUE;

      subscribeInKlaviyo(email)
        .then(function () {
          track('discovery_box_signup', { location: pendingForm.getAttribute('data-location') || '' });
          return addBox(variantId, attributes, email);
        })
        .catch(function () {
          signupButton.removeAttribute('aria-busy');
          showSignupError('Something went wrong. Please try again.');
        });
    });
  }

  /* ---------- Headline variant ---------- */

  function initHeadline() {
    var variant = (new URLSearchParams(window.location.search).get('headline') || '').toLowerCase();
    var headline = document.querySelector('[data-db-headline]');
    if (!headline || !/^[abc]$/.test(variant)) return;
    var text = headline.getAttribute('data-headline-' + variant);
    if (text) headline.innerHTML = text;
  }

  /* ---------- Sticky bar ---------- */

  function initSticky() {
    var bar = document.querySelector('[data-db-sticky]');
    var buybox = document.querySelector('[data-db-buybox]');
    if (!bar || !buybox) return;

    function update() {
      var show = buybox.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('db-sticky-on', show && getComputedStyle(bar).display !== 'none');
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(update).observe(buybox);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Dish cards ---------- */

  function initDishes() {
    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-db-dish-toggle]'));

    function setOpen(toggle, open) {
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = toggle.getAttribute(open ? 'data-label-open' : 'data-label-closed');
      if (panel) panel.hidden = !open;
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggles.forEach(function (other) {
          if (other !== toggle) setOpen(other, false);
        });
        setOpen(toggle, open);
      });
    });
  }

  /* ---------- FAQ ---------- */

  function initFaq() {
    document.querySelectorAll('[data-db-faq]').forEach(function (list) {
      var items = list.querySelectorAll('details');
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ---------- Reel ---------- */

  function initReel() {
    document.querySelectorAll('[data-db-reel]').forEach(function (reel) {
      var video = reel.querySelector('video');
      var mute = reel.querySelector('[data-db-mute]');
      if (!video) return;

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var playing = video.play();
              if (playing && playing.catch) playing.catch(function () {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.3 }).observe(video);
      }

      if (mute) {
        mute.addEventListener('click', function () {
          video.muted = !video.muted;
          mute.textContent = mute.getAttribute(video.muted ? 'data-label-muted' : 'data-label-unmuted');
          if (!video.muted) {
            var playing = video.play();
            if (playing && playing.catch) playing.catch(function () {});
          }
        });
      }
    });
  }

  /* ---------- Delivery ---------- */

  // Wall-clock time in Sydney as a UTC timestamp, so day arithmetic ignores the visitor's timezone
  function sydneyNow() {
    var parts = {};
    new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach(function (part) {
        parts[part.type] = Number(part.value);
      });
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  }

  function formatDay(time) {
    var date = new Date(time);
    return WEEKDAYS[(date.getUTCDay() + 6) % 7] + ' ' + date.getUTCDate() + ' ' + MONTHS[date.getUTCMonth()];
  }

  function formatCutoff(time) {
    var date = new Date(time);
    var hours = date.getUTCHours();
    var minutes = date.getUTCMinutes();
    var clock = (hours % 12 || 12) + (minutes ? ':' + String(minutes).padStart(2, '0') : '') + (hours < 12 ? 'am' : 'pm');
    return formatDay(time) + ', ' + clock;
  }

  // Next delivery: the first day whose order cutoff (orderOffset days before, at cutoffTime) is still ahead
  function nextDelivery(windows) {
    var now = sydneyNow();
    var dayMs = 86400000;
    var today = Math.floor(now / dayMs) * dayMs;

    for (var offset = 1; offset <= 21; offset++) {
      var day = today + offset * dayMs;
      var weekdayIndex = ((new Date(day).getUTCDay() + 6) % 7) + 1;
      var best = null;

      windows.forEach(function (row) {
        if (Number(row.weekdayIndex) !== weekdayIndex) return;
        var time = String(row.cutoffTime || '20:00').split(':');
        var cutoff = day + Number(row.orderOffset || 0) * dayMs + (Number(time[0]) * 60 + Number(time[1] || 0)) * 60000;
        if (cutoff > now && (!best || cutoff > best.cutoff)) best = { day: day, cutoff: cutoff };
      });

      if (best) return best;
    }
    return null;
  }

  function deliveryDays(windows) {
    var seen = {};
    windows.forEach(function (row) {
      seen[Number(row.weekdayIndex)] = true;
    });
    return WEEKDAYS.filter(function (name, index) {
      return seen[index + 1];
    });
  }

  var lookups = {};

  function lookup(postcode) {
    if (lookups[postcode]) return lookups[postcode];
    var endpoint = window.dbShippingWindowsUrl;
    if (!endpoint) return Promise.reject(new Error('no endpoint'));
    var url = new URL(endpoint, window.location.origin);
    url.searchParams.set('postcode', postcode);

    lookups[postcode] = fetch(url.toString(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (payload) {
        var windows = (payload && Array.isArray(payload.data) ? payload.data : []).filter(function (row) {
          return row && row.active === true;
        });
        if (!payload || payload.success === false || !windows.length) return { delivers: false };
        return {
          delivers: true,
          zone: windows[0].zoneTitle || '',
          next: nextDelivery(windows),
          days: deliveryDays(windows),
        };
      });
    lookups[postcode].catch(function () {
      delete lookups[postcode];
    });
    return lookups[postcode];
  }

  var ZONE_NAMES = { SYD: 'Sydney', MEL: 'Melbourne', BNE: 'Brisbane', CBR: 'Canberra', ADL: 'Adelaide', PER: 'Perth' };

  // "SYD METRO" -> "Sydney metro", "CENTRAL COAST" -> "Central Coast"
  function zoneName(zone) {
    var words = String(zone).trim().split(/\s+/);
    if (ZONE_NAMES[words[0]]) {
      return [ZONE_NAMES[words[0]]].concat(words.slice(1).map(function (word) {
        return word.toLowerCase();
      })).join(' ');
    }
    return words
      .map(function (word) {
        return word.charAt(0) + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  function renderNextDelivery(result) {
    document.querySelectorAll('[data-db-next-delivery]').forEach(function (line) {
      if (!result || !result.delivers || !result.next) {
        line.hidden = true;
        return;
      }
      line.innerHTML = line
        .getAttribute('data-template')
        .replace('[zone]', zoneName(result.zone))
        .replace('[date]', formatDay(result.next.day))
        .replace('[cutoff]', formatCutoff(result.next.cutoff));
      line.hidden = false;
    });
  }

  function renderResult(postcode, result) {
    document.querySelectorAll('[data-db-postcode-result]').forEach(function (output) {
      output.hidden = false;
      if (result === 'error') {
        output.setAttribute('data-state', 'error');
        output.textContent = "We couldn't check your postcode just now. Your delivery days show at checkout.";
        return;
      }
      if (!result.delivers) {
        output.setAttribute('data-state', 'error');
        output.textContent = "Désolé, we don't deliver to " + postcode + ' yet.';
        return;
      }
      output.removeAttribute('data-state');
      var text = 'Oui! We deliver to ' + postcode + ' (' + zoneName(result.zone) + ').';
      if (result.next) {
        text += ' Next delivery ' + formatDay(result.next.day) + ' if you order by ' + formatCutoff(result.next.cutoff) + '.';
      }
      if (result.days.length) text += ' Delivery days: ' + result.days.join(', ') + '.';
      output.textContent = text;
    });
  }

  function initDelivery() {
    var forms = document.querySelectorAll('[data-db-postcode]');
    var line = document.querySelector('[data-db-next-delivery]');
    var saved = storageGet('localStorage', POSTCODE_STORE);
    var initial = saved || (line && line.getAttribute('data-default-postcode')) || '';

    forms.forEach(function (form) {
      var input = form.querySelector('input');
      if (saved) input.value = saved;

      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '').slice(0, 4);
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var postcode = input.value.trim();
        forms.forEach(function (other) {
          other.querySelector('input').value = postcode;
        });

        if (!/^\d{4}$/.test(postcode)) {
          document.querySelectorAll('[data-db-postcode-result]').forEach(function (output) {
            output.hidden = false;
            output.setAttribute('data-state', 'error');
            output.textContent = 'Enter a 4-digit postcode.';
          });
          return;
        }

        storageSet('localStorage', POSTCODE_STORE, postcode);
        track('discovery_postcode_check', { postcode: postcode });
        lookup(postcode).then(
          function (result) {
            renderResult(postcode, result);
            if (result.delivers) renderNextDelivery(result);
          },
          function () {
            renderResult(postcode, 'error');
          }
        );
      });
    });

    if (/^\d{4}$/.test(initial)) {
      lookup(initial).then(renderNextDelivery, function () {});
    }
  }

  function init() {
    captureUtms();
    initHeadline();
    initBuy();
    initSticky();
    initDishes();
    initFaq();
    initReel();
    initDelivery();
    initScrollDepth();
    trackViewContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
